import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth, type CrossTenantAccount } from '../context/AuthContext';
import { authService } from '../services/authService';
import { rememberPostAuthRedirect, consumePostAuthRedirect } from '../lib/postAuthRedirect';
import { SIGN_IN_LANDING_STATE } from '../lib/signInLanding';
import { readPendingSchoolCode } from '../lib/pendingSchoolCode';
import { buildTenantLoginUrl } from '../lib/tenantUrls';
import { UserRole } from '../types';
import { Mail, User as UserIcon, Lock, ArrowRight, AlertCircle, Sparkles, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useTenant } from '../context/TenantContext';
import { applyTenantBranding, clearTenantBranding } from '../lib/tenantBranding';
import { loadBrandFont } from '../lib/googleFonts';
import { supabase } from '../lib/supabase';
import {
  TOKENS,
  PathwaysMotif,
  onColour,
  readableOn,
  normaliseBrandColour,
  resolveHeadlineFont,
  loginClasses,
  loginScopeCss,
  heroImageScrim,
  LOGIN_DEFAULT_HEADLINE,
  LOGIN_DEFAULT_SUBTITLE,
} from '../lib/loginTheme';

const MIN_PASSWORD_LENGTH = 8;

// Password strength, NIST 800-63B style: length is the signal, not composition.
// Character-class rules ("must contain a digit") were explicitly deprecated —
// they push people toward Passw0rd! and away from long passphrases. The one
// non-length check is a repetition floor so "aaaaaaaaaaaa" can't score long.
// `tone` rather than a colour class: this page carries its own light/dark
// palette in TOKENS and must not lean on the `dark:` variant (the student shell
// is light-only, and everything else here themes through inline styles).
type StrengthTone = 'danger' | 'warn' | 'ok';
const getPasswordStrength = (
  pw: string,
): { level: 'weak' | 'fair' | 'strong'; bar: string; width: string; tone: StrengthTone; hint: string } => {
  if (pw.length < MIN_PASSWORD_LENGTH)
    return {
      level: 'weak', bar: 'bg-red-500', width: 'w-1/3', tone: 'danger',
      hint: `Too short — use at least ${MIN_PASSWORD_LENGTH} characters.`,
    };
  if (new Set(pw).size < 4)
    return {
      level: 'weak', bar: 'bg-red-500', width: 'w-1/3', tone: 'danger',
      hint: 'Too repetitive — mix in more different characters.',
    };
  if (pw.length < 12)
    return {
      level: 'fair', bar: 'bg-amber-500', width: 'w-2/3', tone: 'warn',
      hint: 'Good. Length beats complexity — a few more characters makes this much stronger.',
    };
  return {
    level: 'strong', bar: 'bg-green-500', width: 'w-full', tone: 'ok',
    hint: 'Strong password.',
  };
};

// Status inks, both themes, each >=4.5:1 on the matching `bg` token.
const STATUS_INK: Record<'light' | 'dark', Record<StrengthTone, string>> = {
  light: { danger: '#B91C1C', warn: '#A16207', ok: '#15803D' },
  dark:  { danger: '#F87171', warn: '#FBBF24', ok: '#4ADE80' },
};

// Supabase surfaces developer-facing strings ("Invalid login credentials"). Map
// the ones a real person can hit onto copy that says what to do next; anything
// unmapped falls through unchanged so we never swallow a genuine error.
type AuthCopyCtx = { canSelfRegister: boolean };

const AUTH_ERROR_COPY: { match: RegExp; copy: string | ((ctx: AuthCopyCtx) => string) }[] = [
  { match: /invalid login credentials/i, copy: "That email and password don't match. Check for typos, or reset your password below." },
  { match: /email_not_confirmed|email not confirmed/i, copy: 'This account isn\u2019t activated yet. Open the activation link we emailed you, then sign in.' },
  { match: /already registered|already been registered|user already exists/i, copy: 'An account with this email already exists. Sign in instead, or reset your password.' },
  // Magic link is sign-in only (shouldCreateUser: false), so an unknown address
  // is a real, answerable state — not the silent broken account it used to be.
  // "Create one first" is only true where the signup mode exists, so the copy
  // branches on canSelfRegister — provisioned-accounts tenants hide signup.
  { match: /signups not allowed for otp|otp_disabled/i,
    copy: ({ canSelfRegister }) => canSelfRegister
      ? "We couldn't find an account with that email. Create one first, then we'll email you a sign-in link."
      : "We couldn't find an account with that email. Check the address, or ask your administrator for access." },
  { match: /for security purposes|rate limit|too many requests/i, copy: 'Too many attempts. Wait a moment and try again.' },
  { match: /password should be at least/i, copy: `Your password is too short — use at least ${MIN_PASSWORD_LENGTH} characters.` },
  { match: /unable to validate email|invalid email/i, copy: "That email address doesn't look right." },
  { match: /failed to fetch|network|networkerror/i, copy: 'Could not reach the server. Check your connection and try again.' },
];

// Matches against the error's stable GoTrue `code` as well as its prose
// message — authService preserves `code` on thrown errors, and GoTrue rewords
// messages between releases while codes are its documented contract.
const friendlyAuthError = (
  err?: { message?: string; code?: string } | null,
  ctx: AuthCopyCtx = { canSelfRegister: true },
): string => {
  const message = err?.message;
  if (!message) return 'Something went wrong. Please try again.';
  const haystack = err?.code ? `${err.code} ${message}` : message;
  const entry = AUTH_ERROR_COPY.find((e) => e.match.test(haystack));
  if (!entry) return message;
  return typeof entry.copy === 'function' ? entry.copy(ctx) : entry.copy;
};

// GoTrue refuses a sign-in on an account whose email is not yet confirmed. The
// code is the contract; the message match covers older GoTrue releases.
const isUnconfirmedError = (err?: { message?: string; code?: string } | null): boolean =>
  err?.code === 'email_not_confirmed' || /email not confirmed/i.test(err?.message ?? '');

// Tokens, the contrast helpers (onColour / readableOn), PathwaysMotif and the
// shared class strings live in lib/loginTheme — the admin preview renders the
// same design and must not re-derive any of it.

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, sendMagicLink, isAuthenticated, user, crossTenantSignout, clearCrossTenantSignout } = useAuth();
  const { tenant, features } = useTenant();

  const schoolName = tenant?.branding?.schoolName;
  const hasCustomBranding = !!(tenant?.logoUrl || schoolName);
  const loginImageUrl = tenant?.branding?.loginImageUrl;
  const loginImagePosition = tenant?.branding?.loginImagePosition || { x: 0.5, y: 0.5 };

  const isDark = tenant?.branding?.loginTheme === 'dark'; // light-first; opt into dark
  const T = TOKENS[isDark ? 'dark' : 'light'];
  const statusInk = STATUS_INK[isDark ? 'dark' : 'light'];

  // Signature colour — leads the hero and the CTA fill. Normalised to a
  // canonical #rrggbb at the source; see normaliseBrandColour for why that
  // matters (this value reaches un-escaped <style> text).
  const brand = normaliseBrandColour(
    (isDark
      ? tenant?.branding?.loginPrimaryColorDark || tenant?.branding?.loginPrimaryColor
      : tenant?.branding?.loginPrimaryColor) || tenant?.branding?.primaryColor,
  );
  // Foreground colour for anything sitting directly on the brand hero / CTA.
  const onBrand = onColour(brand);
  // Contrast-corrected variant for the brand used AS text/outline on the page
  // background: overline, links, secondary button, focus ring.
  const accent = readableOn(brand, T.bg, 4.5, T.ink);

  const loginFontId = tenant?.branding?.loginFontFamily;
  const headlineFont = resolveHeadlineFont(loginFontId);

  useEffect(() => {
    if (loginFontId) loadBrandFont(loginFontId);
  }, [loginFontId]);

  // Apply tenant branding and login theme on login page (outside AppLayout).
  // /login is authoritative over its own theme — it ignores and temporarily
  // overrides any admin dark-mode state from ThemeContext so "light" means
  // light regardless of what the viewer's admin UI looks like.
  useEffect(() => {
    if (!tenant?.branding) return;
    const b = tenant.branding;
    applyTenantBranding(
      b.loginPrimaryColor || b.primaryColor,
      b.secondaryColor,
      b.loginPrimaryColorDark || b.primaryColorDark,
      b.secondaryColorDark,
    );
    const html = document.documentElement;
    const wasDark = html.classList.contains('dark');
    if (isDark) html.classList.add('dark');
    else html.classList.remove('dark');
    return () => {
      clearTenantBranding();
      if (wasDark) html.classList.add('dark');
      else html.classList.remove('dark');
    };
  }, [tenant, isDark]);

  type AuthMode = 'login' | 'signup' | 'magic-link';
  const canSelfRegister = features.selfRegistration !== false;
  const [authMode, setAuthMode] = useState<AuthMode>(
    location.pathname === '/signup' && canSelfRegister ? 'signup' : 'login'
  );
  const isLogin = authMode === 'login';
  const isSignup = authMode === 'signup';
  const isMagicLink = authMode === 'magic-link';

  // If selfRegistration was disabled while user is on signup tab, switch to login
  useEffect(() => {
    if (!canSelfRegister && authMode === 'signup') {
      setAuthMode('login');
    }
  }, [canSelfRegister, authMode]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The account exists — on a DIFFERENT tenant. Set by the password path
  // (TenantMismatchError, slug/name known: the person proved the password) and
  // by the anon pre-flights (slug/name null: existence only, never WHICH
  // tenant). The context's crossTenantSignout covers the third path: a
  // cross-tenant magic link that verified and was signed straight back out.
  const [crossTenant, setCrossTenant] = useState<CrossTenantAccount | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [confirmationSent, setConfirmationSent] = useState(false);
  // The address a sign-in was refused for because it is not activated yet. Set
  // from GoTrue's email_not_confirmed; it turns the error into a way forward
  // (resend) instead of a dead end. Sept 2026: learners who signed up in one
  // browser and opened the activation link in another came back to a tab that
  // said "Invalid email or password" and had no way to ask for another email.
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string | null>(null);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');
  // The "I've clicked the link" retry on the check-your-inbox screen.
  const [activationCheck, setActivationCheck] = useState<'idle' | 'checking' | 'not-yet'>('idle');
  // Path AND query: /redeem?code=… must come back with its code. (ProtectedRoute
  // hands over a full location, so the search was always there to keep.)
  const explicitRedirect = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search ?? ''}`
    : undefined;
  const [name, setName] = useState('');
  // Prefill the email from `?email=` — used by the self-serve signup flow, which
  // lands a freshly-provisioned admin here at <slug>.kulalearn.com/login?email=…
  const [email, setEmail] = useState(() => new URLSearchParams(location.search).get('email') ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  // Password reset is its own view, not a panel nested inside the sign-in form:
  // nesting meant an Enter keypress in the reset field triggered the sign-in
  // form's implicit submit, and it put two brand-filled primary buttons on
  // screen at once.
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const forgotInputRef = useRef<HTMLInputElement>(null);

  // One activation check in flight at a time (button and focus can coincide).
  const activationAttemptRef = useRef(false);
  const lastSilentAttemptRef = useRef(0);
  // The check itself is defined further down with the other handlers; the ref
  // lets the effect below call the latest one without being re-registered.
  const tryActivatedSignInRef = useRef<(silent: boolean) => Promise<void>>(async () => {});

  // Coming back to this tab after the inbox round-trip is the moment to check
  // — silently, so a link that is still on its way costs nothing to look at.
  useEffect(() => {
    if (!confirmationSent) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void tryActivatedSignInRef.current(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [confirmationSent]);

  // Auto-focus the first field when the view changes.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showForgotPassword) forgotInputRef.current?.focus();
      // Arrived with the email filled in (the invite email's button): the
      // password is the only thing left to type.
      else if (authMode === 'login' && emailInputRef.current?.value) passwordInputRef.current?.focus();
      else emailInputRef.current?.focus();
    }, 150);
    return () => clearTimeout(timer);
  }, [authMode, showForgotPassword]);

  const switchMode = (mode: AuthMode) => {
    setAuthMode(mode);
    setError(null);
    setCrossTenant(null);
    clearCrossTenantSignout();
    setMagicLinkSent(false);
    setShowForgotPassword(false);
    setForgotSuccess(false);
    setShowPassword(false);
    setCapsLock(false);
    setUnconfirmedEmail(null);
    setResendState('idle');
    setActivationCheck('idle');
  };

  const openForgotPassword = () => {
    setShowForgotPassword(true);
    setForgotEmail(email);
    setError(null);
    setCrossTenant(null);
    clearCrossTenantSignout();
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setForgotLoading(true);
    try {
      // redirectTo keeps the recovery link on THIS tenant's subdomain and
      // lands it on the change-password page. Must be covered by the Supabase
      // auth Redirect URL allow-list (https://*.kulalearn.com/**) or the link
      // falls back to the Site URL — PasswordRecoveryRedirect catches that too.
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/change-password`,
      });
      if (resetError) throw resetError;
      setForgotSuccess(true);
    } catch (err: any) {
      setError(friendlyAuthError(err));
    } finally {
      setForgotLoading(false);
    }
  };

  const finishSignIn = (loggedInUser: { role?: UserRole } | null | undefined) => {
    // Consume unconditionally: any completed sign-in retires whatever was
    // stored, so a destination remembered for an abandoned email flow can
    // never fire on a later, unrelated login. Router state still wins.
    const storedRedirect = consumePostAuthRedirect();
    const requested = explicitRedirect || storedRedirect;
    if (requested) {
      navigate(requested, { replace: true });
      return;
    }
    // The default landing is marked as a sign-in arrival, so a pass-holding
    // student's Home can forward them to their school's pack.
    navigate(getDefaultLanding(loggedInUser?.role || UserRole.STUDENT), {
      replace: true,
      state: SIGN_IN_LANDING_STATE,
    });
  };

  const resendActivation = async (target: string) => {
    setResendState('sending');
    setError(null);
    try {
      await authService.resendConfirmation(target);
      setResendState('sent');
    } catch (err: any) {
      setResendState('idle');
      setError(friendlyAuthError(err, { canSelfRegister }));
    }
  };

  // The activation link opens wherever the learner's mail client sends it —
  // often a different browser from the one they signed up in — so THIS tab
  // never learns on its own that the account is live. The password is still
  // in memory, so a sign-in attempt is the check: it succeeds the moment the
  // link has been clicked, and fails with email_not_confirmed until then.
  const tryActivatedSignIn = async (silent: boolean) => {
    if (activationAttemptRef.current) return;
    // Focus events come in bursts (alt-tabbing, a notification); one silent
    // look every ten seconds is plenty and stays well inside GoTrue's limits.
    const now = Date.now();
    if (silent && now - lastSilentAttemptRef.current < 10_000) return;
    if (silent) lastSilentAttemptRef.current = now;
    if (!password) {
      // Reloaded mid-flow: the password is gone, so hand over to the form.
      if (!silent) { setConfirmationSent(false); switchMode('login'); }
      return;
    }
    activationAttemptRef.current = true;
    if (!silent) { setActivationCheck('checking'); setError(null); }
    try {
      const loggedInUser = await login(email, password);
      finishSignIn(loggedInUser);
    } catch (err: any) {
      if (isUnconfirmedError(err)) {
        if (!silent) setActivationCheck('not-yet');
      } else if (!silent) {
        setActivationCheck('idle');
        setError(friendlyAuthError(err, { canSelfRegister }));
      }
    } finally {
      activationAttemptRef.current = false;
    }
  };
  // Written after commit, never during render: a discarded render's closure
  // must not become what the committed focus listener calls.
  useEffect(() => {
    tryActivatedSignInRef.current = tryActivatedSignIn;
  });

  if (isAuthenticated) {
    if (user?.isSuperAdmin && tenant?.slug === 'hub') return <Navigate to="/hub" replace />;
    if (user?.role === UserRole.INSTRUCTOR) return <Navigate to="/instructor" replace />;
    if (user?.role === UserRole.ADMIN) return <Navigate to="/admin" replace />;
    return <Navigate to="/browse" replace state={SIGN_IN_LANDING_STATE} />;
  }

  const getDefaultLanding = (userRole: UserRole): string => {
    if (user?.isSuperAdmin && tenant?.slug === 'hub') return '/hub';
    switch (userRole) {
      case UserRole.ADMIN: return '/admin';
      case UserRole.INSTRUCTOR: return '/instructor';
      default: return '/browse';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setCrossTenant(null);
    clearCrossTenantSignout();
    setIsLoading(true);
    try {
      if (isMagicLink) {
        // Pre-flight: an account on ANOTHER tenant would happily receive the
        // link, verify — and be signed straight back out by the tenant guard.
        // Refuse before the inbox round-trip. null (helper failed) and 'none'
        // fall through to GoTrue, which is authoritative.
        if (tenant?.slug) {
          const status = await authService.emailStatus(email, tenant.slug);
          if (status === 'other_tenant') {
            setCrossTenant({ email, tenantSlug: null, tenantName: null });
            return;
          }
        }
        // The magic link comes back through /auth/callback (possibly a new
        // tab), where router state is gone — persist the promised return.
        if (explicitRedirect) rememberPostAuthRedirect(explicitRedirect);
        await sendMagicLink(email);
        setMagicLinkSent(true);
        return;
      }
      let loggedInUser;
      if (isLogin) {
        loggedInUser = await login(email, password);
      } else {
        // TenantContext can finish with tenant === null (the tenants read
        // failed; App only gates on isLoading). A signup submitted then would
        // carry no tenant_slug and be quarantined into 'unattributed' — an
        // account that can never sign in anywhere. Refuse instead.
        if (!tenant?.slug) {
          setError("We couldn't load this site's details. Reload the page and try again.");
          return;
        }
        // Pre-flight: GoTrue answers signup-with-existing-email with a fake
        // success (anti-enumeration), which used to render as "check your
        // inbox" for an email that never comes. Answer honestly instead.
        const status = await authService.emailStatus(email, tenant.slug);
        if (status === 'this_tenant') {
          setError('An account with this email already exists. Sign in instead, or reset your password.');
          return;
        }
        if (status === 'other_tenant') {
          setCrossTenant({ email, tenantSlug: null, tenantName: null });
          return;
        }
        const result = await register(
          name,
          email,
          UserRole.STUDENT,
          password,
          tenant.slug,
          // null = no account yet, so only an unbound code (one this
          // anonymous visitor arrived with) can ride along, never another
          // learner's.
          features.school_passes ? readPendingSchoolCode(null) : null,
        );
        if (result.needsEmailConfirmation) {
          // The verification link lands on /auth/callback in what may be a
          // fresh tab — router state won't survive, localStorage will
          // (Dylan's voucher Jam: "you will be returned" must stay true
          // across the inbox round-trip).
          if (explicitRedirect) rememberPostAuthRedirect(explicitRedirect);
          setConfirmationSent(true);
          return;
        }
        loggedInUser = result.user;
      }
      finishSignIn(loggedInUser);
    } catch (err: any) {
      if (err?.code === 'tenant_mismatch') {
        // Correct password, wrong site — the one case where we can name the
        // account's home and link straight to it.
        setCrossTenant({ email, tenantSlug: err.tenantSlug ?? null, tenantName: err.tenantName ?? null });
      } else {
        // Track the unactivated address for THIS attempt only: a later
        // failure with different credentials must not keep offering a resend
        // for the previous one, nor claim the new one was already emailed.
        setUnconfirmedEmail(isUnconfirmedError(err) ? email : null);
        setResendState('idle');
        setError(friendlyAuthError(err, { canSelfRegister }));
      }
    } finally {
      setIsLoading(false);
    }
  };


  const trackCapsLock = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setCapsLock(e.getModifierState?.('CapsLock') ?? false);
  };

  // Password strength for signup mode
  const strength = isSignup && password.length > 0 ? getPasswordStrength(password) : null;

  // --- Derived copy ---
  const heroHeadline = tenant?.branding?.loginHeadline || LOGIN_DEFAULT_HEADLINE;
  const heroFooter = schoolName || tenant?.name;
  const overline = showForgotPassword
    ? 'Account recovery'
    : isMagicLink ? 'Passwordless' : isLogin ? 'Welcome back' : 'Get started';
  const formHeading = showForgotPassword
    ? 'Reset your password'
    : isMagicLink
      ? 'Sign in with a magic link'
      : isLogin
        ? 'Sign in to your account'
        : 'Create your account';
  // The pitch belongs on the create-account view — a returning user has already
  // bought in, and marketing copy above a password field is noise. The hero
  // panel carries the proposition on every view.
  const formSub = showForgotPassword
    ? "Enter the email you signed up with and we'll send you a link to set a new password."
    : isSignup
      ? (tenant?.branding?.loginSubtitle || LOGIN_DEFAULT_SUBTITLE)
      : null;

  const inputClass = loginClasses.input;
  const inputWithActionClass = loginClasses.inputWithAction;
  const labelClass = loginClasses.label;
  const labelStyle: React.CSSProperties = { color: T.ink2 };
  const secondaryButtonClass = loginClasses.secondaryButton;
  const secondaryButtonStyle: React.CSSProperties = { borderColor: T.line, background: T.surface, color: accent };
  const inlineLinkClass = loginClasses.inlineLink;

  const errorBanner = error ? (
    <div
      role="alert"
      className={`${isDark ? 'bg-red-500/15 border-red-500/30 text-red-200' : 'bg-red-50 border-red-200 text-red-700'} border px-4 py-3 rounded-lg text-[13px] leading-relaxed flex items-start gap-2`}
    >
      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <span>
        {error}
        {unconfirmedEmail && (
          <>
            {' '}
            {resendState === 'sent' ? (
              <span className="font-semibold">Sent — check your inbox.</span>
            ) : (
              <button
                type="button"
                onClick={() => void resendActivation(unconfirmedEmail)}
                disabled={resendState === 'sending'}
                className="font-semibold underline underline-offset-2 disabled:opacity-60"
              >
                {resendState === 'sending' ? 'Sending…' : 'Resend the activation email'}
              </button>
            )}
          </>
        )}
      </span>
    </div>
  ) : null;

  // Cross-tenant notice: local state (password / pre-flight) or the context's
  // post-magic-link signout. Informational amber, not error red — nothing the
  // person typed was wrong; their account just lives on a different site.
  const crossTenantNotice = crossTenant ?? crossTenantSignout;
  const crossTenantUrl = crossTenantNotice?.tenantSlug ? buildTenantLoginUrl(crossTenantNotice.tenantSlug) : null;
  const crossTenantBanner = crossTenantNotice ? (
    <div
      role="alert"
      className={`${isDark ? 'bg-amber-500/15 border-amber-500/30 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800'} border px-4 py-3 rounded-lg text-[13px] leading-relaxed flex items-start gap-2`}
    >
      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <span>
        {crossTenantNotice.tenantName ? (
          <>
            {crossTenantNotice.email ? (
              <>The account for <span className="font-medium">{crossTenantNotice.email}</span> belongs</>
            ) : (
              <>This account belongs</>
            )}{' '}
            to <span className="font-medium">{crossTenantNotice.tenantName}</span>, which has its own sign-in page.
          </>
        ) : (
          <>
            This email already has an account with a different school on this platform. Accounts belong to
            the school site where they were created — sign in there instead.
          </>
        )}
        {crossTenantUrl && (
          <>
            {' '}
            <a href={crossTenantUrl} className="font-semibold underline underline-offset-2">
              Sign in at {new URL(crossTenantUrl).host} →
            </a>
          </>
        )}
      </span>
    </div>
  ) : null;

  const submitLabel = isMagicLink ? 'Send magic link' : isLogin ? 'Sign in' : 'Create account';

  // --- Shared form column ---
  const formColumn = (
    <div className={loginClasses.formColumn}>
      <div>
        <div className={loginClasses.overline} style={{ color: accent }}>
          {overline}
        </div>
        <h1 className={loginClasses.heading} style={{ color: T.ink }}>
          {formHeading}
        </h1>
        {formSub && (
          <p className={loginClasses.sub} style={{ color: T.ink2 }}>
            {formSub}
          </p>
        )}
      </div>

      <div className="mt-6">
        {confirmationSent ? (
          <div className="w-full">
            <CheckCircle className="h-11 w-11 mb-4" style={{ color: accent }} aria-hidden="true" />
            <h2 className="font-sans text-lg font-semibold tracking-[-0.01em] mb-2" style={{ color: T.ink }}>Check your inbox</h2>
            <p className="text-sm mb-1" style={{ color: T.ink2 }}>
              We&rsquo;ve emailed an activation link to <span className="font-medium" style={{ color: T.ink }}>{email}</span>.
            </p>
            <p className="text-sm mb-5" style={{ color: T.ink2 }}>
              Open it on any device &mdash; your phone is fine &mdash; then come back to this tab and we&rsquo;ll sign you in.
            </p>

            {errorBanner}

            {activationCheck === 'not-yet' && (
              <p role="status" className="text-[13px] mb-3" style={{ color: statusInk.warn }}>
                Not activated yet. The email can take a minute to arrive &mdash; check your spam folder, or resend it below.
              </p>
            )}

            <button
              type="button"
              onClick={() => void tryActivatedSignIn(false)}
              disabled={activationCheck === 'checking'}
              className={loginClasses.primaryButton}
              style={{ background: brand, color: onBrand }}
            >
              {activationCheck === 'checking' ? 'Checking…' : "I've clicked the link — sign me in"}
              {activationCheck !== 'checking' && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
            </button>

            <button
              type="button"
              onClick={() => void resendActivation(email)}
              disabled={resendState !== 'idle'}
              className={`${secondaryButtonClass} mt-3`}
              style={secondaryButtonStyle}
            >
              <Mail className="h-4 w-4" aria-hidden="true" />
              {resendState === 'sent' ? 'Sent — check your inbox' : resendState === 'sending' ? 'Sending…' : 'Resend the email'}
            </button>

            <p className="mt-4 text-[13px]" style={{ color: T.ink3 }}>
              Wrong address?{' '}
              <button
                type="button"
                onClick={() => { setConfirmationSent(false); switchMode('signup'); }}
                className={inlineLinkClass}
                style={{ color: accent }}
              >
                Start again
              </button>
              {' '}&middot;{' '}
              <button
                type="button"
                onClick={() => { setConfirmationSent(false); switchMode('login'); }}
                className={inlineLinkClass}
                style={{ color: accent }}
              >
                Back to sign in
              </button>
            </p>
          </div>
        ) : isMagicLink && magicLinkSent ? (
          <div className="w-full">
            <CheckCircle className="h-11 w-11 mb-4" style={{ color: accent }} aria-hidden="true" />
            <h2 className="font-sans text-lg font-semibold tracking-[-0.01em] mb-2" style={{ color: T.ink }}>Check your email</h2>
            <p className="text-sm mb-4" style={{ color: T.ink2 }}>
              We sent a magic link to <span className="font-medium" style={{ color: T.ink }}>{email}</span>. Click the link in the email to sign in.
            </p>
            {/* Returns to the form; it sends nothing, so there is nothing to
                rate-limit. The real limit lives on the next send, where
                Supabase enforces it and friendlyAuthError surfaces it. */}
            <button
              type="button"
              onClick={() => { setMagicLinkSent(false); }}
              className={inlineLinkClass}
              style={{ color: accent }}
            >
              Use a different email
            </button>
          </div>
        ) : showForgotPassword ? (
          /* --- Password reset: a standalone form, so Enter submits THIS. --- */
          forgotSuccess ? (
            <div className="w-full">
              <CheckCircle className="h-11 w-11 mb-4" style={{ color: accent }} aria-hidden="true" />
              <h2 className="font-sans text-lg font-semibold tracking-[-0.01em] mb-2" style={{ color: T.ink }}>Check your email</h2>
              <p className="text-sm mb-4" style={{ color: T.ink2 }}>
                If an account exists for <span className="font-medium" style={{ color: T.ink }}>{forgotEmail}</span>, we&rsquo;ve sent a link to set a new password.
              </p>
              <button
                type="button"
                onClick={() => { setShowForgotPassword(false); setForgotSuccess(false); }}
                className={inlineLinkClass}
                style={{ color: accent }}
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4 w-full">
              <div>
                <label htmlFor="reset-email" className={labelClass} style={labelStyle}>Email</label>
                <div className="relative">
                  <Mail className={loginClasses.inputIcon} style={{ color: T.ink3 }} aria-hidden="true" />
                  <input
                    ref={forgotInputRef}
                    id="reset-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className={inputClass}
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              {errorBanner}

              <button
                type="submit"
                disabled={forgotLoading || !forgotEmail}
                className={loginClasses.primaryButton}
                style={{ background: brand, color: onBrand }}
              >
                {forgotLoading ? 'Sending…' : 'Send reset link'}
              </button>

              <button
                type="button"
                onClick={() => { setShowForgotPassword(false); setError(null); }}
                className={inlineLinkClass}
                style={{ color: T.ink2 }}
              >
                Back to sign in
              </button>
            </form>
          )
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 w-full">
            {isSignup && (
              <div>
                <label htmlFor="name" className={labelClass} style={labelStyle}>Full name</label>
                <div className="relative">
                  <UserIcon className={loginClasses.inputIcon} style={{ color: T.ink3 }} aria-hidden="true" />
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={inputClass}
                    placeholder="Your name"
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className={labelClass} style={labelStyle}>Email</label>
              <div className="relative">
                <Mail className={loginClasses.inputIcon} style={{ color: T.ink3 }} aria-hidden="true" />
                <input
                  ref={emailInputRef}
                  id="email"
                  name="email"
                  type="email"
                  required
                  /* `username` as well as `email` — password managers key the
                     saved credential off the username field. */
                  autoComplete={isSignup ? 'email' : 'username'}
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {!isMagicLink && (
              <div>
                <label htmlFor="password" className={labelClass} style={labelStyle}>Password</label>
                <div className="relative">
                  <Lock className={loginClasses.inputIcon} style={{ color: T.ink3 }} aria-hidden="true" />
                  <input
                    ref={passwordInputRef}
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete={isSignup ? 'new-password' : 'current-password'}
                    /* Only enforced on signup — existing accounts predate this floor. */
                    minLength={isSignup ? MIN_PASSWORD_LENGTH : undefined}
                    aria-describedby={isSignup ? 'password-strength' : undefined}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={trackCapsLock}
                    onKeyUp={trackCapsLock}
                    onBlur={() => setCapsLock(false)}
                    className={inputWithActionClass}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    aria-controls="password"
                    className={loginClasses.inputAction}
                    style={{ color: T.ink3 }}
                  >
                    {showPassword
                      ? <EyeOff className="h-4 w-4" aria-hidden="true" />
                      : <Eye className="h-4 w-4" aria-hidden="true" />}
                  </button>
                </div>

                {capsLock && (
                  <p role="status" className="mt-1.5 text-[12px]" style={{ color: statusInk.warn }}>
                    Caps Lock is on.
                  </p>
                )}

                {strength && (
                  <div className="mt-2" id="password-strength">
                    <div
                      className="h-1 w-full rounded-full overflow-hidden"
                      role="progressbar"
                      aria-label="Password strength"
                      aria-valuetext={strength.level}
                      style={{ background: isDark ? '#27272A' : '#E5E7EB' }}
                    >
                      <div className={`h-full ${strength.bar} ${strength.width} rounded-full transition-all duration-300`} />
                    </div>
                    <p className="text-xs mt-1" style={{ color: statusInk[strength.tone] }}>{strength.hint}</p>
                  </div>
                )}

                {isLogin && (
                  <button
                    type="button"
                    onClick={openForgotPassword}
                    className="mt-0.5 inline-flex items-center min-h-[44px] text-[12.5px] transition-opacity hover:opacity-70"
                    style={{ color: accent }}
                  >
                    Forgot password?
                  </button>
                )}
              </div>
            )}

            {isMagicLink && (
              <p className="text-xs leading-relaxed" style={{ color: T.ink3 }}>
                We&rsquo;ll email you a link that signs you in. No password needed.
              </p>
            )}

            {/* The error sits next to the control that caused it, not at the top
                of the page, and announces itself via role="alert". */}
            {crossTenantBanner ?? errorBanner}

            <div className={isLogin ? '' : 'pt-2'}>
              <button
                type="submit"
                disabled={isLoading}
                className={loginClasses.primaryButton}
                style={{ background: brand, color: onBrand }}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" style={{ color: onBrand }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="sr-only">Working…</span>
                  </>
                ) : (
                  <>
                    {isMagicLink ? <Sparkles className="h-4 w-4" aria-hidden="true" /> : null}
                    {submitLabel}
                    {!isMagicLink ? <ArrowRight className="h-4 w-4" aria-hidden="true" /> : null}
                  </>
                )}
              </button>
            </div>

            {/* Alternative METHOD for the same intent — subordinate to the CTA,
                not a peer tab beside it. */}
            {isLogin && (
              <button type="button" onClick={() => switchMode('magic-link')} className={secondaryButtonClass} style={secondaryButtonStyle}>
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Email me a sign-in link instead
              </button>
            )}
            {isMagicLink && (
              <button type="button" onClick={() => switchMode('login')} className={secondaryButtonClass} style={secondaryButtonStyle}>
                <Lock className="h-4 w-4" aria-hidden="true" />
                Use a password instead
              </button>
            )}
          </form>
        )}
      </div>

      {/* Different INTENT — one quiet line at the foot, not a tab. */}
      {!confirmationSent && !showForgotPassword && !(isMagicLink && magicLinkSent) && canSelfRegister && (
        <p className={loginClasses.footSwitch} style={{ color: T.ink2 }}>
          {isSignup ? (
            <>
              Already have an account?{' '}
              <button type="button" onClick={() => switchMode('login')} className={loginClasses.footSwitchLink} style={{ color: accent }}>
                Sign in
              </button>
            </>
          ) : (
            <>
              New here?{' '}
              <button type="button" onClick={() => switchMode('signup')} className={loginClasses.footSwitchLink} style={{ color: accent }}>
                Create an account
              </button>
            </>
          )}
        </p>
      )}

      {hasCustomBranding && (
        <div className={loginClasses.poweredBy} style={{ color: T.ink3 }}>
          powered by <span className="font-bold">kula</span> learn
        </div>
      )}
    </div>
  );

  // Wordmark on the brand hero. A tenant logo (often reversed-out) sits on the
  // brand colour; otherwise a white kula wordmark.
  const heroWordmark = tenant?.logoUrl ? (
    <img
      src={tenant.logoUrl}
      alt={tenant?.name || 'Learning Platform'}
      className="h-9 w-auto max-w-[180px] object-contain"
    />
  ) : (
    <span className="text-[22px] tracking-[-0.02em]" style={{ color: onBrand }}>
      <span className="font-semibold">kula</span>
      <span className="font-light opacity-75"> learn</span>
    </span>
  );

  return (
    <div className="lgn-scope min-h-screen flex flex-col lg:flex-row" style={{ background: T.bg }}>
      {/* Scoped styling for the brand focus ring + solid inputs. */}
      <style>{loginScopeCss('.lgn-scope', brand, accent, T)}</style>

      {/* Hero panel */}
      <div
        className={loginClasses.heroPanel}
        style={{ background: brand, color: onBrand }}
      >
        {loginImageUrl ? (
          <>
            <img
              src={loginImageUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: `${loginImagePosition.x * 100}% ${loginImagePosition.y * 100}%` }}
            />
            <div className="absolute inset-0" style={{ background: heroImageScrim(brand) }} />
          </>
        ) : (
          <PathwaysMotif colour={onBrand} />
        )}

        <div className="relative z-10">
          {heroWordmark}
        </div>

        <div className="relative z-10">
          <h2
            className={loginClasses.heroHeadline}
            style={{ fontFamily: headlineFont, color: onBrand }}
          >
            {heroHeadline}
          </h2>
          {heroFooter && (
            <div className={loginClasses.heroFooter}>
              {heroFooter}
            </div>
          )}
        </div>
      </div>

      {/* Form panel */}
      <main
        className={loginClasses.formPanel}
        style={{ background: T.bg }}
      >
        {formColumn}
      </main>
    </div>
  );
};
